/**
 * Centralized state management for the verification flow.
 * Replaces 30+ scattered global variables with a single typed state store.
 *
 * @module ui/state/verification-state
 */

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * All possible screen names in the verification flow.
 */
export type ScreenName =
  | 'screen-welcome'
  | 'screen-permissions'
  | 'screen-preparing'
  | 'screen-face-detection'
  | 'screen-liveness'
  | 'screen-liveness-blink'
  | 'screen-age'
  | 'screen-age-result'
  | 'screen-document-type'
  | 'screen-document-capture'
  | 'screen-document-camera'
  | 'screen-document-preview'
  | 'screen-document-back-capture'
  | 'screen-document-back-preview'
  | 'screen-processing'
  | 'screen-consent'
  | 'screen-success'
  | 'screen-failure'
  | 'screen-error';

/**
 * Supported document types for OCR verification.
 */
export type DocumentType = 'id_card' | 'passport' | 'drivers_license';

/**
 * Camera facing mode.
 */
export type CameraFacingMode = 'user' | 'environment';

/**
 * Lighting quality status.
 */
export type LightingStatus = 'good' | 'too_dark' | 'too_bright' | 'low_contrast' | 'unknown';

/**
 * Document side being captured.
 */
export type DocumentSide = 'front' | 'back';

/**
 * Liveness action types.
 */
export type LivenessAction =
  | 'BLINK'
  | 'SMILE'
  | 'LOOK_LEFT'
  | 'LOOK_RIGHT'
  | 'OPEN_MOUTH'
  | 'LOOK_UP'
  | 'LOOK_DOWN'
  | 'RAISE_BROWS';

// =============================================================================
// State Interface
// =============================================================================

/**
 * Screen navigation state.
 */
export interface ScreenState {
  current: ScreenName;
  history: ScreenName[];
}

/**
 * Camera state.
 */
export interface CameraState {
  stream: MediaStream | null;
  facingMode: CameraFacingMode;
}

/**
 * Face detection state.
 */
export interface FaceState {
  detected: boolean;
  quality: number | null;
  selfieImage: string | null; // faceDetectionSelfie (base64 data URL)
  ageImage: string | null; // bestAgeImage (base64 data URL)
}

/**
 * Liveness verification state.
 */
export interface LivenessState {
  currentStep: number;
  steps: LivenessAction[];
  controller: unknown | null; // SDK controller instance
}

/**
 * Age verification state.
 */
export interface AgeState {
  estimated: number | null;
}

/**
 * Document capture state.
 */
export interface DocumentState {
  type: DocumentType | null;
  side: DocumentSide;
  frontImage: string | null; // base64 data URL
  backImage: string | null; // base64 data URL
  cameraStream: MediaStream | null;
  overlayAnimationId: number | null;
  cameraFacingMode: CameraFacingMode;
}

/**
 * S3 upload URLs state.
 */
export interface UploadsState {
  selfie1Url: string | null;
  selfie2Url: string | null;
}

/**
 * Requirements state from backend.
 */
export interface RequirementsState {
  methods: string[] | null;
  data: Record<string, unknown> | null;
}

/**
 * Lighting analysis state.
 */
export interface LightingState {
  status: LightingStatus;
  blocksProgress: boolean;
  checkInterval: number | null;
}

/**
 * Complete verification state interface.
 * Groups all state by logical category.
 */
export interface VerificationState {
  screen: ScreenState;
  camera: CameraState;
  face: FaceState;
  liveness: LivenessState;
  age: AgeState;
  document: DocumentState;
  uploads: UploadsState;
  requirements: RequirementsState;
  consent: boolean | null;
  result: Record<string, unknown> | null;
  callbackUrl: string | null;
  lighting: LightingState;
}

// =============================================================================
// State Change Listener
// =============================================================================

/**
 * Callback invoked when state changes.
 * @param key - The top-level state key that changed
 * @param newValue - The new value
 * @param oldValue - The previous value
 */
export type StateListener<K extends keyof VerificationState = keyof VerificationState> = (
  key: K,
  newValue: VerificationState[K],
  oldValue: VerificationState[K]
) => void;

// =============================================================================
// State Manager Class
// =============================================================================

/**
 * Centralized state manager for the verification flow.
 *
 * Features:
 * - Type-safe get/set/update methods
 * - Observer pattern for reactive updates
 * - Nested object partial updates
 * - Reset to initial state
 * - Debug dump helper
 *
 * @example
 * ```typescript
 * const state = new VerificationStateManager();
 *
 * // Read state
 * const currentScreen = state.get('screen').current;
 *
 * // Update nested state
 * state.update('face', { detected: true, quality: 0.95 });
 *
 * // Subscribe to changes
 * const unsubscribe = state.subscribe((key, newVal, oldVal) => {
 *   console.log(`${key} changed:`, oldVal, '->', newVal);
 * });
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
export class VerificationStateManager {
  private state: VerificationState;
  private listeners: Set<StateListener> = new Set();

  constructor() {
    this.state = this.getInitialState();
  }

  /**
   * Creates the initial state with default values.
   */
  private getInitialState(): VerificationState {
    return {
      screen: {
        current: 'screen-welcome',
        history: [],
      },
      camera: {
        stream: null,
        facingMode: 'user',
      },
      face: {
        detected: false,
        quality: null,
        selfieImage: null,
        ageImage: null,
      },
      liveness: {
        currentStep: 0,
        steps: ['BLINK', 'SMILE'],
        controller: null,
      },
      age: {
        estimated: null,
      },
      document: {
        type: null,
        side: 'front',
        frontImage: null,
        backImage: null,
        cameraStream: null,
        overlayAnimationId: null,
        cameraFacingMode: 'environment',
      },
      uploads: {
        selfie1Url: null,
        selfie2Url: null,
      },
      requirements: {
        methods: null,
        data: null,
      },
      consent: null,
      result: null,
      callbackUrl: null,
      lighting: {
        status: 'unknown',
        blocksProgress: true,
        checkInterval: null,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Core State Access Methods
  // ---------------------------------------------------------------------------

  /**
   * Gets a top-level state value by key.
   * @param key - The state key to retrieve
   * @returns The current value for that key
   */
  get<K extends keyof VerificationState>(key: K): VerificationState[K] {
    return this.state[key];
  }

  /**
   * Sets a top-level state value and notifies listeners.
   * @param key - The state key to set
   * @param value - The new value
   */
  set<K extends keyof VerificationState>(key: K, value: VerificationState[K]): void {
    const oldValue = this.state[key];
    this.state[key] = value;
    this.notify(key, value, oldValue);
  }

  /**
   * Keys that contain object values (can be partially updated).
   */
  private static readonly OBJECT_KEYS: (keyof VerificationState)[] = [
    'screen',
    'camera',
    'face',
    'liveness',
    'age',
    'document',
    'uploads',
    'requirements',
    'lighting',
  ];

  /**
   * Partially updates a nested state object.
   * Only the provided fields are updated; others remain unchanged.
   *
   * Note: This method only works for object-type state keys (screen, camera, face, etc.).
   * For primitive keys (consent, result, callbackUrl), use set() instead.
   *
   * @param key - The state key to update (must be an object-type key)
   * @param partial - Partial object with fields to update
   *
   * @example
   * ```typescript
   * // Only updates 'detected' and 'quality', keeps 'selfieImage' and 'ageImage'
   * state.update('face', { detected: true, quality: 0.95 });
   * ```
   */
  update<
    K extends
      | 'screen'
      | 'camera'
      | 'face'
      | 'liveness'
      | 'age'
      | 'document'
      | 'uploads'
      | 'requirements'
      | 'lighting',
  >(key: K, partial: Partial<VerificationState[K]>): void {
    const current = this.state[key];
    // Shallow merge - works for our flat nested objects
    const newValue = { ...current, ...partial };
    this.set(key, newValue);
  }

  // ---------------------------------------------------------------------------
  // Observer Pattern
  // ---------------------------------------------------------------------------

  /**
   * Subscribes to state changes.
   * @param listener - Callback function invoked on state change
   * @returns Unsubscribe function
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notifies all listeners of a state change.
   */
  private notify<K extends keyof VerificationState>(
    key: K,
    newValue: VerificationState[K],
    oldValue: VerificationState[K]
  ): void {
    this.listeners.forEach((listener) => {
      try {
        listener(key, newValue, oldValue);
      } catch (error) {
        console.error('[VerificationState] Listener error:', error);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Utility Methods
  // ---------------------------------------------------------------------------

  /**
   * Resets all state to initial values.
   * Useful when starting a new verification session.
   */
  reset(): void {
    const initial = this.getInitialState();
    const keys = Object.keys(initial) as (keyof VerificationState)[];

    keys.forEach((key) => {
      // Use set() which handles type-safe assignment and notifications
      // TypeScript needs the explicit cast here due to the generic iteration
      this.set(key, initial[key] as VerificationState[typeof key]);
    });
  }

  /**
   * Returns a shallow copy of the entire state for debugging.
   * @returns Copy of current state
   */
  dump(): VerificationState {
    return { ...this.state };
  }

  /**
   * Returns the number of active listeners.
   * Useful for debugging memory leaks.
   */
  get listenerCount(): number {
    return this.listeners.size;
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

/**
 * Default singleton instance of the state manager.
 * Use this in the browser bundle for global state.
 */
export const verificationState = new VerificationStateManager();
